/**
 * Structural Floor Tool
 * 
 * Structural Floor/Slab Creation by Sketch.
 * Defaults to structural usage.
 */

import { v4 as uuidv4 } from 'uuid';
import { FloorElement, FloorGeometryData, Layer, DeckProfile } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';

export interface StructuralFloorToolOptions {
    floorType: string;
    thickness: number; // mm
    levelId: string;
    offset: number; // mm
    structural: boolean;
}

export class StructuralFloorTool extends BaseTool {
    id = 'structural_floor';
    name = 'Structural Floor';
    cursor = 'crosshair';

    private points: THREE.Vector3[] = [];
    private currentPoint: THREE.Vector3 | null = null;

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersection(event);
        if (!point) return;

        const snapped = this.snapToGrid(point);
        this.points.push(snapped);

        // Check if closed (clicked near start)
        if (this.points.length > 2) {
            const start = this.points[0];
            if (snapped.distanceTo(start) < 0.2) {
                // Close loop
                this.points.pop(); // Remove the last point which is close to start
                this.finishSketch();
                return;
            }
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const point = this.getIntersection(event);
        if (point) {
            this.currentPoint = this.snapToGrid(point);
            this.updatePreview();
        }
    }

    onPointerUp(): void { }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Enter') {
            if (this.points.length >= 3) {
                this.finishSketch();
            }
        } else if (event.key === 'Escape') {
            if (this.points.length > 0) {
                this.points = [];
                this.updatePreview();
            } else {
                super.onKeyDown(event);
            }
        }
    }

    private updatePreview() {
        if (!this.context) return;

        if (this.points.length > 0 && this.currentPoint) {
            const previewPoints = [...this.points, this.currentPoint];
            this.context.appStore.setToolPreview({
                type: 'polygon',
                points: previewPoints,
                closed: false,
                color: '#ff0000' // Red for structural
            });
        } else {
            this.context.appStore.setToolPreview(null);
        }
    }

    private finishSketch() {
        if (!this.context) return;

        // Convert 3D points to 2D boundary
        const boundary: [number, number][] = this.points.map(p => [p.x, p.y]);

        // Options driven by the shared tool properties (OptionsBar edits these),
        // falling back to the BIM-default 150mm concrete slab.
        const thickness = this.context.bimStore.toolProperties.structuralFloorThickness ?? 150;
        const options: StructuralFloorToolOptions = {
            floorType: `Floor: Concrete Slab ${thickness}mm`,
            thickness,
            levelId: 'Level 1',
            offset: 0,
            structural: true
        };

        const floor = createStructuralFloor(boundary, options);
        this.context.bimStore.addElement(floor);

        // Reset
        this.points = [];
        this.context.appStore.setToolPreview(null);
    }
}

export function createStructuralFloor(
    boundary: [number, number][],
    options: StructuralFloorToolOptions
): FloorElement {
    const layers = getFloorLayers(options.floorType);
    const thicknessM = layers.reduce((sum: number, layer: Layer) => sum + layer.thickness, 0);

    // Calculate Span Direction (First segment)
    let spanDir: [number, number, number] = [1, 0, 0];
    if (boundary.length > 1) {
        const p1 = boundary[0];
        const p2 = boundary[1];
        const dx = p2[0] - p1[0];
        const dy = p2[1] - p1[1];
        const len = Math.sqrt(dx * dx + dy * dy);
        if (len > 0.001) {
            spanDir = [dx / len, dy / len, 0];
        }
    }

    // Default Deck Profile
    const deckProfile: DeckProfile = {
        name: 'Composite 76mm',
        ribHeight: 76,
        ribWidth: 152
    };

    const geometryData: FloorGeometryData = {
        boundary: boundary,
        thickness: thicknessM,
        layers: layers,
        levelId: options.levelId,
        heightOffsetFromLevel: options.offset / 1000,
        spanDirection: spanDir,
        deckProfile: deckProfile
    };

    const analyticalNodes = boundary.map(p => ({
        id: uuidv4(),
        position: [p[0], p[1], options.offset / 1000] as [number, number, number],
        boundaryConditions: 'Pinned' as const
    }));

    const analyticalModel = {
        nodes: analyticalNodes,
        surfaces: [{
            boundaryNodeIds: analyticalNodes.map(n => n.id),
            thickness: thicknessM,
            spanDirection: spanDir // Use calculated span
        }]
    };

    const structuralMaterial = {
        id: 'mat-concrete-slab',
        name: 'Concrete - C25/30',
        type: 'Concrete' as const,
        properties: {
            youngsModulus: 29000,
            shearModulus: 12000,
            poissonRatio: 0.2,
            density: 2400,
            thermalExpansionCoefficient: 0.00001,
            concreteCompression: 25
        }
    };

    const rebarHostData = {
        validHost: true,
        coverSettings: {
            top: 25,
            bottom: 25,
            sides: 25
        }
    };

    const floor: FloorElement = {
        id: uuidv4(),
        category: 'Floors',
        type: options.floorType,
        parameters: {
            'Thickness': `${thicknessM.toFixed(2)} m`,
            'Level': options.levelId,
            'Height Offset From Level': `${options.offset} mm`,
            'Structural': options.structural,
            'Area': calculatePolygonArea(boundary).toFixed(2) + ' m²',
            'Perimeter': calculatePolygonPerimeter(boundary).toFixed(2) + ' m',
            'Structural Material': 'Concrete',
            'Structural Deck Profile': deckProfile.name,
            'Span Direction X': spanDir[0].toFixed(3),
            'Span Direction Y': spanDir[1].toFixed(3)
        },
        constraints: [],
        geometryData,
        analyticalModel,
        structuralMaterial,
        rebarHostData
    };

    return floor;
}

function calculatePolygonArea(points: [number, number][]): number {
    let area = 0;
    const n = points.length;
    for (let i = 0; i < n; i++) {
        const j = (i + 1) % n;
        area += points[i][0] * points[j][1];
        area -= points[j][0] * points[i][1];
    }
    return Math.abs(area) / 2;
}

function calculatePolygonPerimeter(points: [number, number][]): number {
    let perimeter = 0;
    const n = points.length;
    for (let i = 0; i < n; i++) {
        const j = (i + 1) % n;
        const dx = points[j][0] - points[i][0];
        const dy = points[j][1] - points[i][1];
        perimeter += Math.sqrt(dx * dx + dy * dy);
    }
    return perimeter;
}

function getFloorLayers(floorType: string): Layer[] {
    // Parse "Floor: Concrete Slab {N}mm" / "Floor: Generic {N}mm" patterns so
    // OptionsBar thickness edits flow into the compound structure.
    const parsed = /(\d+(?:\.\d+)?)\s*mm/.exec(floorType);
    const thicknessM = parsed ? Math.max(Number(parsed[1]) / 1000, 0.02) : 0.15;
    const materialId = floorType.includes('Generic') ? 'Generic' : 'Concrete';
    return [{ function: 'Structure', materialId, thickness: thicknessM, wraps: false }];
}
